import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {DocumentData} from "types/Document";
import DocumentForm from '../Form';

function MutateDocument() {
    const {id} = useParams()
    const {data: document, isLoading} = useQuery(
        ['documents', {id}],
        () => ngage.documents.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: DocumentData) => ngage.documents.store(data))
    const update = useMutation((data: DocumentData) => ngage.documents.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: DocumentData, {setSubmitting}: FormikHelpers<DocumentData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('documents.all')

        setSubmitting(false)
        navigate('/documents')
    }

    // Handle store mutation
    async function handleStore(values: DocumentData) {
        try {
            await store.mutateAsync(values)
            notify("success", "Document Created", `${values.name} has been created`)
        } catch (error) {
            notify("error", "Document Not Created", `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: DocumentData) {
        try {
            await update.mutateAsync(values)
            notify("success", "Document Updated", `${values.name} has been updated`)
            await invalidate(['documents', {id}])
        } catch (error) {
            notify("error", "Document Not Updated", `${values.name} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Admin': '/', 'Documents': '/documents', 'New Document': '/documents/new'}
    const editBreadcrumbs = {'Admin': '/', 'Documents': '/documents', 'Edit Document': `/documents/${id}/edit`}

    return (
        <Page title={id ? "Edit Document" : "New Document"}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <DocumentForm document={document} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateDocument;